import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Col,
  Collapse,
  Divider,
  Dropdown,
  Flex,
  Form,
  FormInstance,
  FormListFieldData,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Tabs,
  TabsProps,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useWatch } from "antd/es/form/Form";
import { ApiService } from "api/api.service";
import {
  ResolutionType,
  PaymentType,
  ProgramSplitFormat,
  CreditorPayFormat,
  StrategyConfigV2,
  StrategyFormat,
  TargetWeeklyCalculationStrategy,
  CreditorSplitFormat,
  InterestCalculationStrategy,
  StrategyRuleKeys,
  InitialPaymentPercentageOf,
  defaultStrategyValue,
} from "dtos/strategy.dto";
import { useEffect, useMemo, useState } from "react";
import {
  TbArrowDown,
  TbArrowLeft,
  TbArrowUp,
  TbCalendarRepeat,
  TbChevronRight,
  TbCircleCheck,
  TbCircleX,
  TbChevronsRight,
  TbColumns,
  TbDotsVertical,
  TbEdit,
  TbFlagCheck,
  TbInfoCircle,
  TbMathFunction,
  TbPercentage,
  TbPlus,
  TbReceiptTax,
  TbScale,
  TbSettingsAutomation,
  TbTableOptions,
  TbTargetArrow,
  TbTrash,
  TbTrophy,
  TbUsersGroup,
  TbX,
  TbFileText,
  TbEye,
} from "react-icons/tb";
import { useSearchParams } from "react-router-dom";
import { Merge } from "types/_utils.types";
import { formatMoney, toCapitalCase, toTitleCase } from "utils/functions";
import ModifyParameters, { CustomParameters } from "./CustomizeParams";

type T_TabKeys = "custom-strategy" | "dynamic-strategies";

const { Panel } = Collapse;

const EditDynamicStrategy = ({
  form,
  strategy,
  readonly,
  onFinish,
  selectedOrg,
}: {
  form: FormInstance<typeof defaultStrategyValue>;
  strategy?: StrategyConfigV2 & { title: string; dynamic_pushback_toggle: boolean };
  readonly: boolean;
  onFinish: (values: typeof defaultStrategyValue) => unknown;
  selectedOrg: string | undefined;
}) => {
  const [pbToggle, setPbToggle] = useState(
    strategy?.dynamic_pushback_toggle ?? defaultStrategyValue.dynamic_pushback_toggle,
  );
  const [customizeParameters, setCustomizeParameters] = useState<CustomParameters[]>([]);

  const getOrganisationDetails = ApiService.organisation.useGetBySlug(selectedOrg);
  const pandaDocConfig = getOrganisationDetails.data?.pandadoc_mappings ?? [];
  const base = useWatch(["program_split", "meta", "base"], form) ?? "debt";
  const is_bias_exclusive = useWatch(["is_bias_exclusive"], form) ?? defaultStrategyValue.is_bias_exclusive;
  const initial_fee_base = useWatch(["initial_fee_base"], form) ?? defaultStrategyValue.initial_fee_base;

  const pctProps = {
    step: 0.01,
    min: 0,
    max: 1,
    formatter: (value: any) => (value ? `${(value * 100).toFixed(0)}%` : "0%"),
    parser: (value: any) => (value ? Number.parseFloat(value.replace("%", "")) / 100 : 0),
    className: "w-full",
  };
  const getProgramSplitTitle = (str: ProgramSplitFormat) => {
    switch (str) {
      case ProgramSplitFormat.UNIFIED:
        return "Unified";
      case ProgramSplitFormat.EARLY_STOPPING_WEEKS:
        return "Weeks";
      case ProgramSplitFormat.EARLY_STOPPING_PERCENTAGE:
        return "Percentage";
      case ProgramSplitFormat.PERCENTAGE_OF_WEEKLY:
        return "Percentage";
      default:
        return "Unknown";
    }
  };
  const splitFormat = Form.useWatch(["program_split", "format"], form);
  const interestStrategy = Form.useWatch(["interest_calculation_strategy"], form);

  useEffect(() => {
    const activeCustomParams: CustomParameters[] = [];
    if (strategy?.service_fee?.length) activeCustomParams.push("service_fee");
    if (strategy?.legal_fee?.length) activeCustomParams.push("legal_fee");

    if (activeCustomParams.length) setCustomizeParameters(activeCustomParams);
  }, [strategy?.service_fee, strategy?.legal_fee]);

  return (
    <Form
      form={form}
      disabled={readonly}
      className="bg-white h-full py-4"
      layout="vertical"
      initialValues={strategy ?? defaultStrategyValue}
      onFinish={(values) => onFinish({ ...values, dynamic_pushback_toggle: pbToggle })}
    >
      <Flex vertical className="px-2">
        <Row>
          <Col span={24}>
            <Form.Item name="key" label="Title" rules={[{ required: true, min: 1 }]}>
              <Input placeholder="Standard Program v1" />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item name="description" label="Description">
              <Input.TextArea autoSize={{ minRows: 1, maxRows: 1 }} placeholder="Optional strategy details..." />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item name="template_id" label="PandaDoc Template">
              <Select
                style={{ width: "100%" }}
                placeholder="Select PandaDoc template for this strategy"
                options={pandaDocConfig.map((config) => ({
                  label: config.title,
                  value: config._id,
                }))}
                popupRender={(menu) => (
                  <>
                    {menu}

                    <Divider style={{ margin: "8px 0" }} />

                    <div style={{ padding: 8 }}>
                      <Button
                        type="primary"
                        block
                        onClick={() =>
                          window.open("/dashboard/application/key-mapping?org_code=" + selectedOrg, "_blank")
                        }
                      >
                        + Create New Template
                      </Button>
                    </div>
                  </>
                )}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="resolution_type"
              label={
                <Space>
                  <TbTargetArrow /> Type
                </Space>
              }
            >
              <Select
                options={Object.values(ResolutionType).map((v) => ({
                  label: toTitleCase(v.replace("-", "_"), { sep: "_", forceLower: true }),
                  value: v,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="format"
              label={
                <Space>
                  <TbSettingsAutomation /> Format
                </Space>
              }
            >
              <Select
                options={Object.values(StrategyFormat).map((v) => ({
                  label: toTitleCase(v.replace("-", "_"), { sep: "_", forceLower: true }),
                  value: v,
                  disabled: v === StrategyFormat.MANAGERS_APPROVAL,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="payment_type"
              label={
                <Space>
                  <TbCalendarRepeat /> Payment
                </Space>
              }
            >
              <Select
                options={Object.values(PaymentType).map((v) => ({
                  label: toTitleCase(v.replace("-", "_"), { sep: "_", forceLower: true }),
                  value: v,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="target_weekly_calculation_strategy"
              label={
                <Space>
                  <TbMathFunction /> Week Calculation
                </Space>
              }
            >
              <Select
                options={Object.values(TargetWeeklyCalculationStrategy).map((v) => ({
                  label: toTitleCase(v, { sep: "_", forceLower: true }),
                  value: v,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
      </Flex>

      <Collapse ghost defaultActiveKey={["fees"]}>
        <Panel
          forceRender
          header={
            <Space>
              <TbTableOptions /> Fees & Financials
            </Space>
          }
          key="fees"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <TbFlagCheck /> Initial Fee (%)
                  </Space>
                }
              >
                <Row gutter={8}>
                  <Col span={18}>
                    <Form.Item name="initial_fee">
                      <InputNumber {...pctProps} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name="initial_fee_base" noStyle>
                      <Button
                        className="w-full"
                        onClick={() =>
                          form.setFieldValue(
                            "initial_fee_base",
                            initial_fee_base === InitialPaymentPercentageOf.TOTAL_INTEREST
                              ? InitialPaymentPercentageOf.TOTAL_DEBT
                              : InitialPaymentPercentageOf.TOTAL_INTEREST,
                          )
                        }
                        type="primary"
                        variant="solid"
                        color={initial_fee_base === InitialPaymentPercentageOf.TOTAL_INTEREST ? "geekblue" : "purple"}
                      >
                        of {initial_fee_base === InitialPaymentPercentageOf.TOTAL_INTEREST ? "Interest" : "Debt"}
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <TbScale /> Upfront Amount
                  </Space>
                }
              >
                <Row gutter={8}>
                  <Col span={8}>
                    <Form.Item name="is_bias_exclusive" noStyle>
                      <Button
                        className="w-full"
                        onClick={() => form.setFieldValue("is_bias_exclusive", !is_bias_exclusive)}
                        // icon={pbToggle ? <TbArrowUp /> : <TbArrowDown />}
                        type="primary"
                        variant="solid"
                        color={is_bias_exclusive ? "orange" : "cyan"}
                      >
                        {is_bias_exclusive ? "Exclusive" : "Inclusive"}
                      </Button>
                    </Form.Item>
                  </Col>
                  <Col span={16}>
                    <Form.Item name="initial_bias_amount">
                      <InputNumber prefix="$" className="w-full" min={0} />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="interest_calculation_strategy"
                label={
                  <Space>
                    <TbPercentage /> Interest Calculation
                  </Space>
                }
              >
                <Select
                  options={Object.values(InterestCalculationStrategy).map((v) => ({
                    label: toTitleCase(v, { sep: "_", forceLower: true }),
                    value: v,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <TbChevronsRight /> Dynamic Pushback (%)
                  </Space>
                }
              >
                <Row gutter={8}>
                  <Col span={8}>
                    <Button
                      className="w-full"
                      onClick={() => setPbToggle((prev) => !prev)}
                      icon={pbToggle ? <TbArrowUp /> : <TbArrowDown />}
                      type="primary"
                      variant="solid"
                      color={pbToggle ? "green" : "red"}
                    >
                      {pbToggle ? "Increase" : "Decrease"}
                    </Button>
                  </Col>
                  <Col span={16}>
                    <Form.Item name="dynamic_weekly_pushback" noStyle>
                      <InputNumber {...pctProps} />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
          </Row>
          {interestStrategy === InterestCalculationStrategy.PERCENTAGE ? (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="program_fee"
                  label={
                    <Space>
                      <TbReceiptTax /> Program Fee (%)
                    </Space>
                  }
                >
                  <InputNumber {...pctProps} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="success_fee"
                  label={
                    <Space>
                      <TbTrophy /> Success Fee (%)
                    </Space>
                  }
                >
                  <InputNumber {...pctProps} />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="interest_amount"
                  label={
                    <Space>
                      <TbReceiptTax /> Interest Amount
                    </Space>
                  }
                >
                  <InputNumber prefix="$" className="w-full" min={0} />
                </Form.Item>
              </Col>
            </Row>
          )}
        </Panel>
        <Panel
          forceRender
          header={
            <Space>
              <TbColumns /> Program Split
            </Space>
          }
          key="program"
        >
          <div>
            <div className="grid grid-cols-2 gap-4">
              <Form.Item name={["program_split", "format"]} label="Split Format">
                <Select
                  options={Object.values(ProgramSplitFormat)
                    .filter((format) => format !== ProgramSplitFormat.PERCENTAGE_OF_REVENUE)
                    .map((v) => ({
                      label: toTitleCase(v, { sep: "_", forceLower: true }),
                      value: v,
                    }))}
                />
              </Form.Item>
              <Form.Item
                name={["program_split", "initial_payment_timeline"]}
                label={
                  <Space.Compact>
                    Initial Payment Timeline
                    <Tooltip title="Initial payment will be divided in x + 1 weeks">
                      <TbInfoCircle className="ml-2" />
                    </Tooltip>
                  </Space.Compact>
                }
              >
                <InputNumber className="w-full" min={0} />
              </Form.Item>
            </div>
          </div>
          {[ProgramSplitFormat.EARLY_STOPPING_WEEKS].includes(splitFormat) && (
            <Flex className="justify-between">
              <Form.Item
                name={["program_split", "meta", "base"]}
                initialValue={defaultStrategyValue.program_split?.meta?.base ?? "debt"}
                className="mb-3 w-48"
                label="Base"
                layout="horizontal"
              >
                <Select
                  size="small"
                  options={[
                    {
                      label: "Total Debt",
                      value: "debt",
                    },
                    { label: "Total Interest", value: "interest" },
                  ]}
                  onChange={(value) => {
                    const multiplier = value === "interest" ? 0.25 : 4;
                    const steps = form.getFieldValue(["program_split", "meta", "steps"]);
                    const modified = steps.map((step: Partial<{ debt_cap: number; weeks: number }>) => ({
                      debt_cap: Math.ceil((step.debt_cap ?? 0) * multiplier),
                      weeks: Math.ceil((step.weeks ?? 0) * multiplier),
                    }));

                    form.setFieldValue(["program_split", "meta", "steps"], modified);
                  }}
                />
              </Form.Item>
              {splitFormat === ProgramSplitFormat.EARLY_STOPPING_WEEKS ? (
                <Form.Item
                  name={["program_split", "meta", "mode"]}
                  initialValue={defaultStrategyValue.program_split?.meta?.mode ?? "variable"}
                  className="mb-3"
                >
                  <Segmented
                    options={[
                      { label: "Fixed for all ranges", value: "fixed" },
                      { label: "Variable by debt range", value: "variable" },
                    ]}
                  />
                </Form.Item>
              ) : null}
            </Flex>
          )}
          {splitFormat === ProgramSplitFormat.EARLY_STOPPING_WEEKS ? (
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) => {
                return (
                  prev.program_split?.meta?.mode !== curr.program_split?.meta?.mode ||
                  prev.program_split?.meta?.steps !== curr.program_split?.meta?.steps
                );
              }}
            >
              {({ getFieldValue }) => {
                const mode = getFieldValue(["program_split", "meta", "mode"]) ?? "variable";

                if (mode === "fixed") {
                  return (
                    <div className="bg-gray-50 flex items-start gap-4 rounded">
                      <Form.Item
                        name={["program_split", "meta", "fixed_weeks"]}
                        label="All debt ranges"
                        rules={[{ required: true }]}
                        className="w-1/2"
                      >
                        <InputNumber suffix="weeks" min={1} className="w-full" />
                      </Form.Item>
                    </div>
                  );
                }

                return (
                  <Form.List
                    name={["program_split", "meta", "steps"]}
                    initialValue={(defaultStrategyValue.program_split.meta?.steps ?? []) as unknown[]}
                  >
                    {(fields) => {
                      const liveSteps =
                        getFieldValue(["program_split", "meta", "steps"]) ??
                        defaultStrategyValue.program_split?.meta?.steps ??
                        [];

                      const items: FormListFieldData[] =
                        fields.length > 0
                          ? fields
                          : liveSteps.map((_: unknown, i: number) => ({
                              key: i,
                              name: i,
                              fieldKey: i,
                              isListField: true,
                            }));

                      return (
                        <div className="bg-gray-50 flex items-start gap-4 rounded">
                          {items.map(({ key, name, ...restField }) => {
                            const isLast = liveSteps.length - 1 === key;

                            const threshold = isLast
                              ? (liveSteps[key - 1]?.debt_cap ?? 0)
                              : (liveSteps[key]?.debt_cap ?? 0);

                            return (
                              <div key={key} className="mb-2 flex flex-col items-start">
                                <Form.Item
                                  {...restField}
                                  name={[name, "weeks"]}
                                  label={`${toCapitalCase(base)} ${isLast ? ">" : "≤"} ${formatMoney(threshold)}`}
                                  rules={[{ required: true }]}
                                >
                                  <InputNumber style={{ width: 150 }} suffix="weeks" min={1} />
                                </Form.Item>
                              </div>
                            );
                          })}
                        </div>
                      );
                    }}
                  </Form.List>
                );
              }}
            </Form.Item>
          ) : (
            <Form.Item
              hidden={["unified", "unknown"].includes(getProgramSplitTitle(splitFormat).toLocaleLowerCase())}
              name={["program_split", "meta", "percentage"]}
              label={getProgramSplitTitle(splitFormat)}
            >
              <InputNumber
                disabled={["unified", "unknown"].includes(getProgramSplitTitle(splitFormat).toLocaleLowerCase())}
                className="w-full"
                min={0}
              />
            </Form.Item>
          )}
        </Panel>

        <Panel
          forceRender
          header={
            <Space>
              <TbUsersGroup /> Creditor Settings
            </Space>
          }
          key="creditor"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name={["creditor_split", "total_payback"]} label="Payback Ratio">
                <InputNumber {...pctProps} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["creditor_split", "initial_bias_amount"]} label="Creditor Upfront Amount">
                <InputNumber prefix="$" className="w-full" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name={["creditor_split", "format"]} label="Amount Format">
                <Select
                  options={Object.values(CreditorSplitFormat).map((v) => ({
                    label: toTitleCase(v, { sep: "_", forceLower: true }),
                    value: v,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["creditor_split", "pay_format"]} label="Pay Via">
                <Select
                  options={Object.values(CreditorPayFormat).map((v) => ({
                    label: toTitleCase(v, { sep: "_", forceLower: true }),
                    value: v,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </Panel>

        <Panel
          forceRender
          header={
            <Space>
              <TbEye /> Display Timeline Rules
            </Space>
          }
          key="timeline"
        >
          <Col span={12}>
            <Form.Item name="fallback_display_timeline" label={<Space>Display Timeline Multiplier (%)</Space>}>
              <InputNumber {...pctProps} />
            </Form.Item>
          </Col>
        </Panel>

        <Panel
          forceRender
          header={
            <Space>
              <TbMathFunction /> Custom Parameters
            </Space>
          }
          key="custom"
        >
          <Flex gap={8}>
            {!customizeParameters.includes("service_fee") && (
              <Button
                size="small"
                variant="outlined"
                color="orange"
                onClick={() => setCustomizeParameters((prev) => [...prev, "service_fee"])}
              >
                Modify Service Fee
              </Button>
            )}
            {!customizeParameters.includes("legal_fee") && (
              <Button
                size="small"
                variant="outlined"
                color="red"
                onClick={() => setCustomizeParameters((prev) => [...prev, "legal_fee"])}
              >
                Modify Legal Fee
              </Button>
            )}
          </Flex>

          <Row className="my-4" gutter={[16, 16]}>
            {customizeParameters.map((param) => (
              <ModifyParameters
                name={param}
                slug={param}
                key={`customize_param_${param}`}
                onRemove={() => {
                  form.setFieldValue([param], undefined);
                  setCustomizeParameters((prev) => prev.filter((k) => k !== param));
                }}
              />
            ))}
          </Row>
        </Panel>

        <Panel
          forceRender
          header={
            <Space>
              <TbFileText /> Rules
            </Space>
          }
          key="rules"
        >
          <Form.List name="rules">
            {(fields, { add, remove }) => {
              const rules = form.getFieldValue("rules") ?? [];

              const usedKeys = new Set(rules.map((r: any) => r?.key).filter(Boolean));

              const allRuleOptions = Object.entries(StrategyRuleKeys).map(([label, value]) => ({
                label,
                value,
              }));

              const availableRuleOptions = allRuleOptions.filter(({ value }) => !usedKeys.has(value));

              return (
                <>
                  {fields.map(({ key, name, ...restField }) => {
                    const currentKey = rules[name]?.key;
                    const rowOptions = allRuleOptions.filter(
                      ({ value }) => !usedKeys.has(value) || value === currentKey,
                    );

                    return (
                      <Row key={key} gutter={16} align="middle">
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "key"]}
                            label="Rule"
                            rules={[{ required: true, message: "Key is required" }]}
                          >
                            <Select options={rowOptions} placeholder="e.g. Max Program Tenure" />
                          </Form.Item>
                        </Col>
                        <Col span={14}>
                          <Form.Item
                            {...restField}
                            name={[name, "value"]}
                            label="Value"
                            rules={[{ required: true, message: "Value is required" }]}
                          >
                            <InputNumber className="w-full" />
                          </Form.Item>
                        </Col>
                        <Col span={2}>
                          <Button type="text" danger icon={<TbTrash />} onClick={() => remove(name)} />
                        </Col>
                      </Row>
                    );
                  })}
                  <Form.Item>
                    <Button
                      type="dashed"
                      disabled={availableRuleOptions.length === 0}
                      onClick={() =>
                        availableRuleOptions.length > 0 &&
                        add({
                          key: availableRuleOptions[0].value,
                          value: 0,
                        })
                      }
                      block
                      icon={<TbPlus />}
                    >
                      Add Rule
                    </Button>
                  </Form.Item>
                </>
              );
            }}
          </Form.List>
        </Panel>
      </Collapse>
    </Form>
  );
};

const meta: Record<
  string,
  {
    prefix?: string;
    suffix?: string;
    values: Record<string, { label: string; color: string }>;
  }
> = {
  resolution_type: {
    suffix: "Plan",
    values: {
      [ResolutionType.NEGOTIATE]: {
        label: "Negotiation",
        color: "purple",
      },
      [ResolutionType.SETTLEMENT]: {
        label: "Settlement",
        color: "green",
      },
    },
  },
  format: {
    suffix: "Format",
    values: {
      [StrategyFormat.DEFAULT]: {
        label: "Fixed",
        color: "orange",
      },
      [StrategyFormat.CUSTOM]: {
        label: "Dynamic",
        color: "geekblue",
      },
      [StrategyFormat.MANAGERS_APPROVAL]: {
        label: "Custom",
        color: "red",
      },
    },
  },
  payment_type: {
    suffix: "Payment",
    values: {
      [PaymentType.ONE_TIME]: {
        label: "Full Upfront",
        color: "blue",
      },
      ["interest-upfront"]: {
        label: "Interest Upfront",
        color: "lime",
      },
      [PaymentType.WEEKLY]: {
        label: "Weekly",
        color: "magenta",
      },
      [PaymentType.MONTHLY]: {
        label: "Monthly",
        color: "purple",
      },
    },
  },
};

const pickMeta = (key: string, value: string) => {
  let { prefix, suffix, values } = meta[key] ?? {};
  prefix ??= "";
  suffix ??= "";
  values ??= {};
  let { color, label } = values?.[value] ?? {};
  label ||= toCapitalCase(value);

  return {
    label: [prefix, label, suffix].join(" "),
    color,
  };
};

export const StrategyCardFooterTags = ({
  values,
  className,
  noColor = false,
  variant = "outlined",
}: {
  className?: string;
  noColor?: boolean;
  variant?: "solid" | "outlined";
  values: Partial<
    Merge<
      Pick<
        StrategyConfigV2,
        | "resolution_type"
        | "format"
        | "payment_type"
        | "target_weekly_calculation_strategy"
        | "interest_calculation_strategy"
      >,
      { payment_type: PaymentType | "interest-upfront" }
    >
  >;
}) => {
  return (
    <Flex className={className}>
      {Object.entries(values).map(([key, value]) => {
        const { label, color } = pickMeta(key, value);

        return (
          <Tag
            key={key}
            color={noColor ? undefined : `${color}${variant === "solid" ? "-inverse" : ""}`}
            className="text-xs lowercase"
          >
            {label}
          </Tag>
        );
      })}
    </Flex>
  );
};

const StrategyV2Card = (
  strategy: StrategyConfigV2 & { title: string; className?: string; onClick: () => unknown; orgCode: string },
) => {
  const disabled = Boolean(strategy.meta?.disabled ?? false);
  const updateStrategyConfig = ApiService.strategy.useEditStrategyConfig({ orgCode: strategy.orgCode });

  const isInterestUpfront = strategy.initial_fee === strategy.program_fee + strategy.success_fee;
  const isDisabled = strategy.meta?.unavailable === true || strategy.meta?.disabled === true;

  const defaultDescription = () => {
    return `No description provided`;
  };

  return (
    <Flex
      className={`items-center justify-between px-4 rounded-lg mt-2 border border-gray-300 border-solid py-2 ${strategy.className ?? ""}`}
    >
      <Flex vertical onClick={strategy.onClick} className={`cursor-pointer w-full ${isDisabled && "opacity-70"}`}>
        <Space>
          <Typography.Title className="h-4" level={5}>
            {strategy.title}
          </Typography.Title>
          {isDisabled ? (
            <Tag color="red" className="uppercase px-1 text-xs">
              Disabled
            </Tag>
          ) : null}
        </Space>
        <Typography.Text
          italic={!strategy.description?.length}
          className={`${strategy.description?.length ? "text-gray-500" : "text-gray-400"} text-xs line-clamp-1`}
        >
          {strategy.description || defaultDescription()}
        </Typography.Text>
        <StrategyCardFooterTags
          noColor={isDisabled}
          values={{
            resolution_type: strategy.resolution_type,
            format: strategy.format,
            payment_type:
              strategy.payment_type === PaymentType.WEEKLY && isInterestUpfront
                ? "interest-upfront"
                : strategy.payment_type,
            // target_weekly_calculation_strategy: strategy.target_weekly_calculation_strategy,
            // interest_calculation_strategy: strategy.interest_calculation_strategy,
          }}
          className="mt-2"
        />
      </Flex>

      <Flex>
        <Dropdown
          menu={{
            items: [
              {
                type: "item",
                key: "disable",
                icon: disabled ? <TbCircleCheck size={16} /> : <TbCircleX size={16} />,
                label: disabled ? "Enable" : "Disable",
                onClick() {
                  const payload = { ...strategy };
                  payload.meta ??= {};
                  payload.meta.disabled = !disabled;
                  updateStrategyConfig.mutate(payload, {
                    onSuccess() {
                      message.success(`Successfully ${disabled ? "enabled" : "disabled"} this strategy`);
                    },
                  });
                },
              },
            ],
          }}
        >
          <Button icon={<TbDotsVertical />} size="small" />
        </Dropdown>
      </Flex>
    </Flex>
  );
};

const ListDynamicStrategies = (props: {
  strategies: StrategyConfigV2[];
  loading: boolean;
  refetch: () => unknown;
  selectedOrg: string;
}) => {
  const [selectedDynamicStrategy, setSelectedDynamicStrategy] = useState<string | undefined>();
  const [isDynamicStrategyEditing, setIsDynamicStrategyEditing] = useState(false);
  const [searchParams] = useSearchParams();
  const slug = searchParams.get("slug") ?? "";
  const editStrategyConfig = ApiService.strategy.useEditStrategyConfig({ orgCode: slug });
  const createStrategyConfig = ApiService.strategy.useCreateStrategyConfig({ orgCode: slug });
  const deleteStrategyConfig = ApiService.strategy.useDeleteStrategyConfig({ orgCode: slug });
  const [filters, setFilters] = useState<Partial<Pick<StrategyConfigV2, "format" | "resolution_type">>>({
    format: StrategyFormat.DEFAULT,
  });

  const filteredStrategies = useMemo(() => {
    return props.strategies.filter((strategy) => {
      for (const filter in filters) {
        const value = filters[filter as keyof typeof filters];

        if (value !== undefined && value !== strategy[filter as keyof typeof strategy]) {
          return false;
        }
      }
      return true;
    });
  }, [props.strategies, filters]);

  const addStrategyTitle = (
    strategy: StrategyConfigV2,
    fallback = "",
  ): StrategyConfigV2 & { title: string; dynamic_pushback_toggle: boolean } => ({
    ...strategy,
    title: strategy.key || fallback,
    dynamic_pushback_toggle: strategy.dynamic_weekly_pushback >= 0,
    dynamic_weekly_pushback: Math.abs(strategy.dynamic_weekly_pushback),
  });

  const showList = !isDynamicStrategyEditing && !selectedDynamicStrategy;
  const ListAllStrategies = () => (
    <div>
      <div>
        <div className="flex justify-end mb-2">
          <Segmented
            value={filters.format}
            onChange={(format?: StrategyFormat) => setFilters((prev) => ({ ...prev, format }))}
            options={[
              { label: "All", value: undefined },
              { label: toTitleCase(StrategyFormat.DEFAULT), value: StrategyFormat.DEFAULT },
              { label: toTitleCase(StrategyFormat.CUSTOM), value: StrategyFormat.CUSTOM },
            ]}
          />
        </div>
      </div>
      <Flex className="mb-4" vertical>
        {filteredStrategies.map((strategy, index) => (
          <StrategyV2Card
            onClick={() => {
              setSelectedDynamicStrategy(strategy.slug);
              setIsDynamicStrategyEditing(true);
            }}
            {...addStrategyTitle(strategy, `Dynamic Plan ${index + 1}`)}
            key={strategy.slug}
            orgCode={slug}
          />
        ))}
      </Flex>
    </div>
  );

  const EditStrategy = ({
    selectedStrategy,
    unselect,
    selectedOrg,
  }: {
    selectedStrategy: StrategyConfigV2 | undefined;
    unselect: () => unknown;
    selectedOrg: string;
  }) => {
    const [form] = Form.useForm<typeof defaultStrategyValue>();
    const readOnly = false; // for now, also while adding new it will always be false
    const queryClient = useQueryClient();

    return (
      <div className="">
        <Flex gap={6} className="w-full justify-between items-center pb-2">
          <Flex gap={6}>
            <Button
              size="small"
              onClick={() => {
                setSelectedDynamicStrategy(undefined);
                setIsDynamicStrategyEditing(false);
              }}
            >
              <TbArrowLeft />
            </Button>
            <Tag>{selectedStrategy?.slug || "New Strategy"}</Tag>
          </Flex>
          <Flex gap={6}>
            {selectedStrategy?.slug ? (
              <Button
                icon={<TbTrash />}
                onClick={() => {
                  Modal.confirm({
                    title: "Are you sure you want to delete this strategy?",
                    onOk: () => {
                      deleteStrategyConfig.mutate(
                        { slug: selectedStrategy.slug },
                        {
                          onSuccess() {
                            props.refetch();
                            message.success(`${selectedStrategy.key} deleted successfully!`);
                            unselect();
                          },
                        },
                      );
                    },
                  });
                }}
                variant="solid"
                color="pink"
                size="small"
              >
                Delete
              </Button>
            ) : null}
          </Flex>
        </Flex>
        <EditDynamicStrategy
          form={form}
          strategy={selectedStrategy ? addStrategyTitle(selectedStrategy) : undefined}
          readonly={readOnly}
          onFinish={(values: typeof defaultStrategyValue) => {
            const payload = {
              ...defaultStrategyValue,
              ...values,
            };

            payload.display_timeline![payload.display_timeline!.length - 1].debt_cap = 999999999999999;
            payload.dynamic_weekly_pushback *= payload.dynamic_pushback_toggle ? 1 : -1;

            if (selectedStrategy?.slug) {
              editStrategyConfig.mutate(
                { slug: selectedStrategy.slug, ...payload },
                {
                  onSuccess() {
                    props.refetch();
                    message.success("Strategy config updated");
                    queryClient.invalidateQueries({ queryKey: ["get-strategy-configs"] });
                    setSelectedDynamicStrategy(undefined);
                    setIsDynamicStrategyEditing(false);
                  },
                },
              );
            } else {
              createStrategyConfig.mutate(payload, {
                onSuccess() {
                  props.refetch();
                  message.success("New Strategy config created");
                  queryClient.invalidateQueries({ queryKey: ["get-strategy-configs"] });
                  setSelectedDynamicStrategy(undefined);
                  setIsDynamicStrategyEditing(false);
                },
              });
            }
          }}
          selectedOrg={selectedOrg}
        />
        <Button
          onClick={() => {
            form.submit();
          }}
          type="primary"
          block
          className="mt-2 mb-2"
        >
          Save Changes
        </Button>
      </div>
    );
  };

  if (props.loading) return <div className="max-w-6xl flex flex-col justify-between h-64 mx-auto px-6"></div>;

  return (
    <div className="max-w-6xl flex flex-col justify-between mx-auto px-6">
      <div className="">
        {showList ? (
          <ListAllStrategies />
        ) : (
          <EditStrategy
            unselect={() => {
              setSelectedDynamicStrategy(undefined);
              setIsDynamicStrategyEditing(false);
            }}
            selectedStrategy={props.strategies.find((strategy) => strategy.slug === selectedDynamicStrategy)}
            selectedOrg={props.selectedOrg}
          />
        )}
      </div>
      <Button
        block
        type="dashed"
        onClick={() => {
          if (isDynamicStrategyEditing) setSelectedDynamicStrategy(undefined);
          setIsDynamicStrategyEditing((prev) => !prev);
        }}
        icon={isDynamicStrategyEditing ? <TbX /> : selectedDynamicStrategy ? <TbEdit /> : <TbPlus />}
        className=""
      >
        {isDynamicStrategyEditing
          ? "Cancel Modifications"
          : selectedDynamicStrategy
            ? "Edit Strategy"
            : "Add New Strategy"}
      </Button>
    </div>
  );
};

export default function StrategyModal({
  open,
  onClose,
  customStrategyConfigForm,
  dynamicStrategyConfigForm,
  refetchOrg,
  selectedOrg,
}: Readonly<{
  open: boolean;
  onClose: () => void;
  customStrategyConfigForm: FormInstance;
  dynamicStrategyConfigForm: FormInstance;
  refetchOrg: () => void;
  selectedOrg: string;
}>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const slug = searchParams.get("slug") ?? "";
  const [currentTab, setCurrentTab] = useState<T_TabKeys>("dynamic-strategies");

  const updateStrategyOrganisation = ApiService.organisation.useUpdateOrganisationStrategyConfig();
  const getAllStrategyConfigs = ApiService.strategy.useGetAllStrategyConfigs({ orgCode: slug });

  const items: TabsProps["items"] = [
    {
      key: "dynamic-strategies",
      label: "Dynamic Strategies",
      children: (
        <ListDynamicStrategies
          strategies={getAllStrategyConfigs.data ?? []}
          loading={getAllStrategyConfigs.isLoading}
          refetch={getAllStrategyConfigs.refetch}
          selectedOrg={selectedOrg}
        />
      ),
    },
    {
      key: "custom-strategy",
      label: "Custom Strategy",
      children: (
        <Form
          layout="vertical"
          form={customStrategyConfigForm}
          onFinish={(values) => {
            updateStrategyOrganisation.mutate(
              { slug, body: values, field: "custom" },
              {
                onSuccess: () => {
                  message.success("Strategy config updated succesfully.");
                  customStrategyConfigForm.resetFields();
                  setSearchParams((prev) => {
                    const newParams = new URLSearchParams(prev);
                    newParams.delete("slug");
                    return newParams;
                  });
                  onClose();

                  refetchOrg();
                },
              },
            );
          }}
          style={{
            height: 600,
            overflow: "auto",
            paddingInline: 24,
          }}
        >
          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="lawsuit_initial" label="Lawsuit Initial" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item name="lawsuit_monthly" label="Lawsuit Monthly" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="insurance_one_time" label="Insurance One-Time">
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item name="insurance_monthly" label="Insurance Monthly" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <Form.Item name="service_weekly" label="Service Weekly" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item name="card_mdr" label="Card MDR" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="multiplier" label="Multiplier" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="total_interest" label="Total Interest" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <Form.Item name="initial_pay" label="Initial Pay" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item name="program_part" label="Program Part" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="success_part" label="Success Part" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item name="bias_amount" label="Bias Amount">
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="pushback_factor" label="Pushback Factor">
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="weekly_pushback" label="Weekly Pushback">
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </div>
        </Form>
      ),
    },
    {
      key: "fixed-strategies",
      label: "Fixed Strategies",
      children: (
        <Form
          layout="vertical"
          form={dynamicStrategyConfigForm}
          onFinish={(values) => {
            updateStrategyOrganisation.mutate(
              { slug, body: values.strategies, field: "dynamic" },
              {
                onSuccess: () => {
                  message.success("Dynamic strategies updated successfully.");
                  dynamicStrategyConfigForm.resetFields();
                  setSearchParams((prev) => {
                    const newParams = new URLSearchParams(prev);
                    newParams.delete("slug");
                    return newParams;
                  });
                  onClose();
                  refetchOrg();
                },
              },
            );
          }}
          style={{
            height: 600,
            overflow: "auto",
            paddingInline: 24,
          }}
        >
          <Form.List name="strategies">
            {(fields, { add, remove }) => {
              return (
                <>
                  <Collapse
                    accordion
                    expandIcon={(props) => (
                      <TbChevronRight
                        size={24}
                        className="transition-transform"
                        style={{
                          transform: `rotate(${props?.isActive ? 90 : 0}deg)`,
                        }}
                      />
                    )}
                    items={fields.map(({ key, name, ...restField }) => {
                      return {
                        key,
                        label: (
                          <div className="flex justify-between items-center">
                            <p>{`Strategy ${name + 1}`}</p>
                            <Button
                              icon={<TbTrash />}
                              disabled={fields.length === 1}
                              danger
                              onClick={() => remove(name)}
                              size="small"
                            ></Button>
                          </div>
                        ),
                        children: (
                          <div key={key} className="border p-4 mb-4 rounded-lg">
                            <div className="grid grid-cols-2 gap-4">
                              <Form.Item
                                {...restField}
                                name={[name, "lawsuit_initial"]}
                                label="Lawsuit Initial"
                                rules={[{ required: true }]}
                              >
                                <InputNumber style={{ width: "100%" }} />
                              </Form.Item>

                              <Form.Item
                                {...restField}
                                name={[name, "lawsuit_monthly"]}
                                label="Lawsuit Monthly"
                                rules={[{ required: true }]}
                              >
                                <InputNumber style={{ width: "100%" }} />
                              </Form.Item>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <Form.Item {...restField} name={[name, "insurance_one_time"]} label="Insurance One-Time">
                                <InputNumber style={{ width: "100%" }} />
                              </Form.Item>

                              <Form.Item
                                {...restField}
                                name={[name, "insurance_monthly"]}
                                label="Insurance Monthly"
                                rules={[{ required: true }]}
                              >
                                <InputNumber style={{ width: "100%" }} />
                              </Form.Item>
                            </div>
                            <div className="grid grid-cols-4 gap-4">
                              <Form.Item
                                {...restField}
                                name={[name, "service_weekly"]}
                                label="Service Weekly"
                                rules={[{ required: true }]}
                              >
                                <InputNumber style={{ width: "100%" }} />
                              </Form.Item>

                              <Form.Item
                                {...restField}
                                name={[name, "card_mdr"]}
                                label="Card MDR"
                                rules={[{ required: true }]}
                              >
                                <InputNumber style={{ width: "100%" }} />
                              </Form.Item>

                              <Form.Item
                                {...restField}
                                name={[name, "multiplier"]}
                                label="Multiplier"
                                rules={[{ required: true }]}
                              >
                                <InputNumber style={{ width: "100%" }} />
                              </Form.Item>

                              <Form.Item
                                {...restField}
                                name={[name, "total_interest"]}
                                label="Total Interest"
                                rules={[{ required: true }]}
                              >
                                <InputNumber style={{ width: "100%" }} />
                              </Form.Item>
                            </div>
                            <div className="grid grid-cols-4 gap-4">
                              <Form.Item
                                {...restField}
                                name={[name, "initial_pay"]}
                                label="Initial Pay"
                                rules={[{ required: true }]}
                              >
                                <InputNumber style={{ width: "100%" }} />
                              </Form.Item>

                              <Form.Item
                                {...restField}
                                name={[name, "program_part"]}
                                label="Program Part"
                                rules={[{ required: true }]}
                              >
                                <InputNumber style={{ width: "100%" }} />
                              </Form.Item>

                              <Form.Item
                                {...restField}
                                name={[name, "success_part"]}
                                label="Success Part"
                                rules={[{ required: true }]}
                              >
                                <InputNumber style={{ width: "100%" }} />
                              </Form.Item>

                              <Form.Item {...restField} name={[name, "bias_amount"]} label="Bias Amount">
                                <InputNumber style={{ width: "100%" }} />
                              </Form.Item>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <Form.Item {...restField} name={[name, "pushback_factor"]} label="Pushback Factor">
                                <InputNumber style={{ width: "100%" }} />
                              </Form.Item>

                              <Form.Item {...restField} name={[name, "weekly_pushback"]} label="Weekly Pushback">
                                <InputNumber style={{ width: "100%" }} />
                              </Form.Item>
                            </div>
                          </div>
                        ),
                      };
                    })}
                  />

                  <Form.Item>
                    <Button
                      className="mt-4"
                      type="dashed"
                      onClick={() =>
                        add({
                          lawsuit_initial: 995,
                          lawsuit_monthly: 145,
                          insurance_one_time: 800,
                          insurance_monthly: 145,
                          service_weekly: 25,
                          card_mdr: 0,
                          multiplier: 0.78,
                          total_interest: 0,
                          initial_pay: 0.78,
                          program_part: 0,
                          success_part: 0,
                        })
                      }
                      block
                    >
                      + Add Strategy
                    </Button>
                  </Form.Item>
                </>
              );
            }}
          </Form.List>
        </Form>
      ),
    },
  ];

  return (
    <Modal
      centered
      width={950}
      // className="h-56"
      title="Strategy Config"
      destroyOnHidden
      open={open}
      onCancel={() => {
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("slug");
          return newParams;
        });
        onClose();
      }}
      footer={
        currentTab === "custom-strategy" ? (
          <Button
            disabled={updateStrategyOrganisation.isPending}
            type="primary"
            block
            onClick={() => {
              customStrategyConfigForm.submit();
            }}
          >
            Save Custom Strategy
          </Button>
        ) : currentTab === "dynamic-strategies" ? null : (
          <Button
            disabled={updateStrategyOrganisation.isPending}
            type="primary"
            block
            onClick={() => {
              dynamicStrategyConfigForm.submit();
            }}
          >
            Save Fixed Strategies
          </Button>
        )
      }
      styles={{
        body: {
          height: 600,
          overflowY: "auto",
        },
        content: {
          paddingInline: 0,
        },
        header: {
          paddingInline: 24,
        },
        footer: {
          paddingInline: 24,
        },
      }}
    >
      <Tabs
        activeKey={currentTab}
        onChange={(activeKey) => {
          setCurrentTab(activeKey as T_TabKeys);
        }}
        items={items}
        // style={{
        //   paddingInline: 24,
        // }}
        tabBarStyle={{
          paddingInline: 24,
        }}
        // className="!px-6"
      />
    </Modal>
  );
}
